import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  ImageBackground,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { gql, useQuery } from "@apollo/client";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { useLinkTo } from "@react-navigation/native";
import { Text, View } from "../components/Themed";
import Config from "../constants/Config";
import useColorScheme from "../hooks/useColorScheme";
import i18n from "../utils/localization";
import { getProductImageUrl } from "../utils/urls";
import PublicFooter from "../components/PublicFooter";

const LANDING_MENU = gql`
  query LandingMenu {
    onlineMenu {
      categories {
        id
        name
      }
      products {
        id
        category
        title
        subtitle
        imageUrls
        prices
        variants
      }
    }
  }
`;

const fallbackHero = require("../assets/images/splash.png");
const landingLanguageOrder = ["en", "bg", "it"];
const landingLanguageLabels = {
  en: "EN",
  bg: "BG",
  it: "IT",
};

function normalizeLocaleCode(value) {
  const raw = String(value || "en").trim();
  if (!raw) {
    return "en";
  }

  return raw.toLowerCase().split("-")[0];
}

function getLandingLanguages() {
  const configured = typeof i18n.getLanguages === "function" ? i18n.getLanguages() : [];
  const normalized = Array.isArray(configured)
    ? configured.map(normalizeLocaleCode).filter(Boolean)
    : [];
  return Array.from(new Set([...landingLanguageOrder, ...normalized]));
}

function translateDefault(value, fallback = value) {
  const source = value || fallback || "";
  return i18n.t(source) || source;
}

function getLandingCopy() {
  const brandName = Config.brandName || String(Config.menuTitle || "Venue").replace(/\s+Menu$/i, "");
  return {
    brandName: translateDefault(brandName),
    title: translateDefault(Config.brandTitle, brandName),
    eyebrow: translateDefault(Config.landingEyebrow, "Order, pickup, delivery"),
    tagline: translateDefault(Config.landingTagline, "Order online, check the menu, or continue into the venue app."),
  };
}

function priceLabel(product) {
  const price = Number(product?.prices?.[0] || 0);
  return price > 0 ? `${price.toFixed(2)} EUR` : "";
}

function pickFeaturedProducts(products) {
  const foodWords = ["бургер", "паста", "пиадина", "сандвич", "салата", "ризото", "десерт"];
  const food = products.filter((product) => {
    const title = String(product.title || "").toLowerCase();
    return foodWords.some((word) => title.includes(word));
  });
  const withImages = food.filter((product) => product.imageUrls?.length);
  return (withImages.length ? withImages : food.length ? food : products).slice(0, 6);
}

function ActionButton({ icon, label, onPress, primary }) {
  return (
    <TouchableOpacity
      style={[styles.actionButton, primary && styles.actionButtonPrimary]}
      onPress={onPress}
    >
      <MaterialIcons name={icon} size={20} color={primary ? "white" : "#111827"} />
      <Text style={[styles.actionButtonText, primary && styles.actionButtonTextPrimary]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

export default function LandingScreen() {
  const linkTo = useLinkTo();
  const colorScheme = useColorScheme();
  const window = useWindowDimensions();
  const [locale, setLocaleState] = useState(() => normalizeLocaleCode(i18n.getLocale()));
  const { loading, data } = useQuery(LANDING_MENU, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    let mounted = true;
    const syncLocale = () => {
      if (mounted) {
        setLocaleState(normalizeLocaleCode(i18n.getLocale()));
      }
    };
    const timer = setTimeout(syncLocale, 250);
    syncLocale();
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, []);

  const products = data?.onlineMenu?.products || [];
  const featuredProducts = useMemo(() => pickFeaturedProducts(products), [products]);
  const landingCopy = useMemo(() => getLandingCopy(), [locale]);
  const landingLanguages = useMemo(() => getLandingLanguages(), []);
  const heroProduct = featuredProducts.find((product) => product.imageUrls?.length);
  const heroSource = heroProduct?.imageUrls?.[0]
    ? { uri: getProductImageUrl(heroProduct.imageUrls[0], { width: 1600, height: 950, fit: "cover", quality: 82 }) }
    : fallbackHero;
  const heroHeight = Math.min(Math.max(window.height * 0.72, 500), 700);
  const compact = window.width < 760;
  const activeLocale = normalizeLocaleCode(locale);
  const changeLanguage = async (language) => {
    const nextLocale = normalizeLocaleCode(language);
    await i18n.setLocale(nextLocale);
    setLocaleState(nextLocale);
  };

  return (
    <SafeAreaView style={styles.screen} edges={["top", "left", "right"]}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        <ImageBackground source={heroSource} style={[styles.hero, { minHeight: heroHeight }]} imageStyle={styles.heroImage}>
          <View style={styles.heroShade} />
          <View style={styles.nav}>
            <Text style={styles.brand}>{landingCopy.brandName}</Text>
            <View style={styles.navControls}>
              <View style={styles.languageBar}>
                {landingLanguages.map((language) => {
                  const selected = activeLocale === language;
                  return (
                    <TouchableOpacity
                      key={language}
                      style={[styles.languageButton, selected && styles.languageButtonActive]}
                      onPress={() => {
                        void changeLanguage(language);
                      }}
                    >
                      <Text style={[styles.languageButtonText, selected && styles.languageButtonTextActive]}>
                        {landingLanguageLabels[language] || language.toUpperCase()}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={styles.navActions}>
                <TouchableOpacity style={styles.navIconButton} onPress={() => linkTo("/menu")}>
                  <MaterialIcons name="restaurant-menu" size={22} color="white" />
                </TouchableOpacity>
              </View>
            </View>
          </View>

          <View style={[styles.heroCopy, compact && styles.heroCopyCompact]}>
            <Text style={styles.eyebrow}>{landingCopy.eyebrow}</Text>
            <Text style={styles.heroTitle}>{landingCopy.title}</Text>
            <Text style={styles.heroText}>{landingCopy.tagline}</Text>
            <View style={styles.heroActions}>
              {Config.modules.onlineOrdering && (
                <ActionButton primary icon="shopping-cart" label={translateDefault("Order now")} onPress={() => linkTo("/order")} />
              )}
              <ActionButton icon="menu-book" label={translateDefault("Menu")} onPress={() => linkTo("/menu")} />
            </View>
          </View>
        </ImageBackground>

        <View style={styles.quickBand}>
          <View style={[styles.quickGrid, compact && styles.quickGridCompact]}>
            {Config.modules.onlineOrdering && (
              <TouchableOpacity style={styles.quickItem} onPress={() => linkTo("/order")}>
                <MaterialIcons name="shopping-bag" size={26} color={Config.companyColor || "#dc2626"} />
                <Text style={styles.quickTitle}>{translateDefault("Online orders")}</Text>
                <Text style={styles.quickText}>{translateDefault("Pickup or delivery")}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.quickItem} onPress={() => linkTo("/menu")}>
              <MaterialIcons name="restaurant-menu" size={26} color={Config.companyColor || "#dc2626"} />
              <Text style={styles.quickTitle}>{translateDefault("Menu")}</Text>
              <Text style={styles.quickText}>{translateDefault("Food and drinks")}</Text>
            </TouchableOpacity>
            {Config.modules.esports && (
              <TouchableOpacity style={styles.quickItem} onPress={() => linkTo("/esports")}>
                <MaterialIcons name="computer" size={26} color={Config.companyColor || "#dc2626"} />
                <Text style={styles.quickTitle}>{translateDefault("E-sports")}</Text>
                <Text style={styles.quickText}>{translateDefault("PC stations")}</Text>
              </TouchableOpacity>
            )}
            {Config.modules.vr && (
              <TouchableOpacity style={styles.quickItem} onPress={() => linkTo("/vr")}>
                <MaterialCommunityIcons name="virtual-reality" size={26} color={Config.companyColor || "#dc2626"} />
                <Text style={styles.quickTitle}>{translateDefault("VR")}</Text>
                <Text style={styles.quickText}>{translateDefault("Virtual reality")}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>{translateDefault("Featured")}</Text>
            {loading && <ActivityIndicator size="small" color={Config.companyColor || "#dc2626"} />}
          </View>
          <View style={[styles.featuredGrid, compact && styles.featuredGridCompact]}>
            {featuredProducts.map((product) => (
              <TouchableOpacity
                key={product.id}
                style={[
                  styles.productCard,
                  compact && styles.productCardCompact,
                  colorScheme === "dark" && styles.productCardDark,
                ]}
                onPress={() => linkTo(`/order?product=${encodeURIComponent(product.id)}`)}
              >
                <ImageBackground
                  source={product.imageUrls?.[0] ? { uri: getProductImageUrl(product.imageUrls[0], { width: 520, height: 320, fit: "cover" }) } : fallbackHero}
                  style={styles.productImage}
                  imageStyle={styles.productImageStyle}
                />
                <View style={styles.productBody}>
                  <Text style={styles.productTitle}>{translateDefault(product.title)}</Text>
                  {!!product.subtitle && <Text style={styles.productSubtitle}>{translateDefault(product.subtitle)}</Text>}
                  <Text style={styles.productPrice}>{priceLabel(product)}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.footerBand}>
          <Text style={styles.footerTitle}>{translateDefault("Ready when you are")}</Text>
          <View style={styles.footerActions}>
            {Config.modules.onlineOrdering && (
              <ActionButton primary icon="shopping-cart" label={translateDefault("Start order")} onPress={() => linkTo("/order")} />
            )}
          </View>
        </View>
        <PublicFooter />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  scroll: {
    flex: 1,
  },
  content: {
    backgroundColor: "#ffffff",
  },
  hero: {
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingTop: 18,
    paddingBottom: 38,
    backgroundColor: "#111827",
  },
  heroImage: {
    resizeMode: "cover",
  },
  heroShade: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.48)",
  },
  nav: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "transparent",
    zIndex: 2,
  },
  brand: {
    color: "white",
    fontSize: 24,
    fontWeight: "900",
    flexShrink: 1,
  },
  navControls: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    backgroundColor: "transparent",
  },
  languageBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    padding: 3,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.5)",
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.22)",
  },
  languageButton: {
    minWidth: 30,
    height: 30,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  languageButtonActive: {
    backgroundColor: "white",
  },
  languageButtonText: {
    color: "#e5e7eb",
    fontSize: 12,
    fontWeight: "900",
  },
  languageButtonTextActive: {
    color: "#111827",
  },
  navActions: {
    flexDirection: "row",
    gap: 8,
    backgroundColor: "transparent",
  },
  navIconButton: {
    width: 42,
    height: 42,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.5)",
    backgroundColor: "rgba(0,0,0,0.22)",
  },
  heroCopy: {
    width: "100%",
    maxWidth: 720,
    backgroundColor: "transparent",
    zIndex: 2,
  },
  heroCopyCompact: {
    maxWidth: "100%",
  },
  eyebrow: {
    color: "#fecaca",
    fontSize: 15,
    fontWeight: "800",
    textTransform: "uppercase",
  },
  heroTitle: {
    color: "white",
    fontSize: 54,
    lineHeight: 60,
    fontWeight: "900",
    marginTop: 10,
  },
  heroText: {
    color: "#e5e7eb",
    fontSize: 19,
    lineHeight: 28,
    marginTop: 12,
    maxWidth: 560,
  },
  heroActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 24,
    backgroundColor: "transparent",
  },
  actionButton: {
    minHeight: 46,
    borderRadius: 8,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "white",
  },
  actionButtonPrimary: {
    backgroundColor: Config.companyColor || "#dc2626",
  },
  actionButtonText: {
    color: "#111827",
    fontWeight: "900",
  },
  actionButtonTextPrimary: {
    color: "white",
  },
  quickBand: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 4,
  },
  quickGrid: {
    maxWidth: 1160,
    width: "100%",
    alignSelf: "center",
    flexDirection: "row",
    gap: 12,
  },
  quickGridCompact: {
    flexWrap: "wrap",
  },
  quickItem: {
    flex: 1,
    minWidth: 180,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    padding: 16,
    gap: 6,
    backgroundColor: "#ffffff",
  },
  quickTitle: {
    fontSize: 17,
    fontWeight: "900",
  },
  quickText: {
    color: "#6b7280",
  },
  section: {
    maxWidth: 1160,
    width: "100%",
    alignSelf: "center",
    paddingHorizontal: 16,
    paddingTop: 28,
    paddingBottom: 20,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 28,
    fontWeight: "900",
  },
  featuredGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  featuredGridCompact: {
    flexDirection: "column",
    flexWrap: "nowrap",
  },
  productCard: {
    minWidth: 260,
    flexBasis: "31%",
    flexGrow: 1,
    borderRadius: 8,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
  },
  productCardCompact: {
    width: "100%",
    minWidth: 0,
    flexBasis: "auto",
    flexGrow: 0,
  },
  productCardDark: {
    borderColor: "#374151",
  },
  productImage: {
    height: 160,
    backgroundColor: "#f3f4f6",
  },
  productImageStyle: {
    resizeMode: "cover",
  },
  productBody: {
    padding: 12,
    gap: 4,
  },
  productTitle: {
    fontSize: 16,
    fontWeight: "900",
  },
  productSubtitle: {
    color: "#6b7280",
  },
  productPrice: {
    marginTop: 4,
    fontWeight: "900",
  },
  footerBand: {
    marginTop: 12,
    paddingHorizontal: 20,
    paddingVertical: 34,
    alignItems: "center",
    gap: 16,
    backgroundColor: "#111827",
  },
  footerTitle: {
    color: "white",
    fontSize: 28,
    fontWeight: "900",
    textAlign: "center",
  },
  footerActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 10,
    backgroundColor: "transparent",
  },
});
